// 主题模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { ThemeState, ThemeActions } from "../types";
import { themeService } from "../services";
import { BaseCache } from "../../base/stores";

// 创建主题模块专用的缓存实例
const themeCache = new BaseCache({
  defaultTTL: 60 * 60 * 1000, // 1小时
  maxSize: 20,
  enableLogging: true,
});

// 初始状态
const initialState: ThemeState = {
  data: null,
  themeLoading: false,
  currentTheme: "light",
  cache: new Map(),
};

// 创建主题store
export const useThemeStore = createWithEqualityFn<ThemeState & ThemeActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // 主题数据相关
    setTheme: config => set({ data: config }),
    setThemeLoading: loading => set({ themeLoading: loading }),
    setCurrentTheme: theme => set({ currentTheme: theme }),
    loadTheme: async themeId => {
      const { setThemeLoading, setTheme } = get();

      try {
        setThemeLoading(true);

        const cacheKey = `theme_config_${themeId || "default"}`;

        // 使用缓存优先的 API 调用
        const data = await themeCache.cacheFirst(
          cacheKey,
          () => themeService.loadTheme(themeId),
          60 * 60 * 1000 // 1小时缓存
        );

        setTheme(data);
        setThemeLoading(false);
        return data;
      } catch (error) {
        console.error("主题配置加载失败:", error);
        setThemeLoading(false);
        throw error;
      }
    },

    // 缓存相关 - 委托给 BaseCache 实例
    setCache: (key, data, ttl) => {
      themeCache.setCache(key, data, ttl);
      set({ cache: new Map(themeCache["cache"]) });
    },

    getCache: key => {
      return themeCache.getCache(key);
    },

    clearCache: pattern => {
      themeCache.clearCache(pattern);
      set({ cache: new Map(themeCache["cache"]) });
    },

    hasCache: key => {
      return themeCache.hasCache(key);
    },

    getCacheKeys: () => {
      return themeCache.getCacheKeys();
    },

    getCacheSize: () => {
      return themeCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return themeCache.cacheFirst(key, apiCall, ttl);
    },
  }))
);

// 选择器hooks
export const useThemeData = () =>
  useThemeStore(state => ({
    theme: state.data,
    loading: state.themeLoading,
    currentTheme: state.currentTheme,
  }));

// 主题服务 hook
export function useThemeService() {
  return useThemeStore(state => ({
    loadTheme: state.loadTheme,
    setTheme: state.setTheme,
    setThemeLoading: state.setThemeLoading,
    setCurrentTheme: state.setCurrentTheme,
  }));
}

// 主题缓存 hook
export function useThemeCache() {
  return useThemeStore(state => ({
    setCache: state.setCache,
    getCache: state.getCache,
    clearCache: state.clearCache,
    hasCache: state.hasCache,
    getCacheKeys: state.getCacheKeys,
    getCacheSize: state.getCacheSize,
    cacheFirst: state.cacheFirst,
  }));
}
